import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../utils/api';

const AuthContext = createContext(null);

// Fallback demo user for when backend is not available
const DEMO_USER = {
  id: 'demo-001',
  name: 'Raja Kumar',
  email: 'raja@gummas.in',
  phone: '9876543210',
  role: 'owner',
  avatar: '',
  salonId: 1,
  salon: { id: 1, name: "Gumma's Glam House" },
  preferences: { language: 'en', theme: 'light' },
};

const PROFILE = 'auth.profile';

/*
 * Only these fields are kept on the device.
 *
 * The cache exists to draw the shell — a name, a role, which salon — before
 * the server answers. It used to store whatever /auth/me returned, and
 * /auth/me returned the whole user row, so every staff phone and counter
 * browser was holding the account's bcrypt hash and its password-reset token
 * in localStorage, permanently. The server no longer sends them; this makes
 * sure that even if something does, it is not written down.
 */
const KEEP = ['id', 'name', 'email', 'phone', 'role', 'avatar', 'salonId', 'branchId', 'preferences'];

const slim = (user) => {
  if (!user || typeof user !== 'object') return null;
  const out = {};
  for (const k of KEEP) if (user[k] !== undefined) out[k] = user[k];
  return out;
};

/** The last profile the server sent, for drawing the shell before it answers again. */
const readCachedUser = () => {
  try {
    const raw = localStorage.getItem(PROFILE);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Anything cached by an older build is rewritten on read, so the hash that
    // is sitting on staff devices today is gone the next time they open it.
    const clean = slim(parsed);
    if (clean && Object.keys(parsed).length !== Object.keys(clean).length) {
      localStorage.setItem(PROFILE, JSON.stringify(clean));
    }
    return clean;
  } catch { return null; }
};

const cacheUser = (user) => {
  try {
    const clean = slim(user);
    if (clean) localStorage.setItem(PROFILE, JSON.stringify(clean));
    else localStorage.removeItem(PROFILE);
  } catch { /* private browsing, or full */ }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [salon, setSalon] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [useDemoMode, setUseDemoMode] = useState(false);

  // On mount, try to restore session
  useEffect(() => {
    // Kept alongside the token so a refresh can draw the app immediately. It is
    // a copy of what the server last said, never the authority for anything.

    const tryGetMe = async (retries = 3, delayMs = 2000) => {
      for (let i = 0; i < retries; i++) {
        try {
          const response = await api.auth.getMe();
          if (response.success) return response;
        } catch (err) {
          const is401 = err?.statusCode === 401 || err?.status === 401 ||
            err?.message?.toLowerCase().includes('unauthorized') ||
            err?.message?.toLowerCase().includes('not authorized');
          if (is401) throw Object.assign(err, { _is401: true });
          if (i < retries - 1) await new Promise(r => setTimeout(r, delayMs));
        }
      }
      throw new Error('Backend unavailable after retries');
    };

    const init = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        /*
         * Show the app first, check with the server second.
         *
         * The session used to be re-fetched before anything rendered, which put
         * a round trip to the API in front of every page refresh — the reason a
         * reload felt slow while moving between pages felt instant. The cached
         * profile is only used to draw the shell; every request still carries
         * the token and the server still decides, so a session that has really
         * expired is corrected a moment later by the call below.
         */
        const cached = readCachedUser();
        if (cached) {
          setUser(cached);
          setSalon(cached.salon || null);
          setToken(storedToken);
          setLoading(false);
        }
        try {
          const response = await tryGetMe();
          setUser(response.data);
          setSalon(response.data.salon || null);
          setToken(storedToken);
          cacheUser(response.data);
        } catch (err) {
          if (err._is401) {
            localStorage.removeItem('token');
            cacheUser(null);
            setToken(null);
            setUser(null);
            setSalon(null);
          } else {
            // Backend temporarily unavailable — keep token, don't use demo mode
            console.warn('Backend unavailable, staying on login');
            setToken(null);
            setUser(null);
          }
        }
      }
      setLoading(false);
    };
    init();

    // Listen for forced logout from API interceptor
    const handleForceLogout = () => {
      // The cached profile has to go with the token, or the next refresh draws
      // the app for somebody who has just been signed out.
      cacheUser(null);
      setToken(null);
      setUser(null);
      setSalon(null);
    };
    window.addEventListener('auth:logout', handleForceLogout);
    return () => window.removeEventListener('auth:logout', handleForceLogout);
  }, []);

  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.auth.login({ email, password });
      if (response.success) {
        const { user: userData, salon: salonData, token: newToken } = response.data;
        localStorage.setItem('token', newToken);
        cacheUser({ ...userData, salon: salonData });
        setToken(newToken);
        setUser(userData);
        setSalon(salonData);
        setUseDemoMode(false);
        setLoading(false);
        return { success: true, user: userData };
      }
    } catch (err) {
      // Only fall back to demo mode for genuine network errors (backend unavailable)
      // err.code is only present on raw axios errors (not stripped response data)
      const isNetworkError = err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED';
      if (isNetworkError) {
        console.warn('Backend unavailable, using demo login');
        const demoToken = 'demo-jwt-token-gummas';
        localStorage.setItem('token', demoToken);
        setToken(demoToken);
        setUser({ ...DEMO_USER, email });
        setSalon(DEMO_USER.salon);
        setUseDemoMode(true);
        setLoading(false);
        return { success: true, user: DEMO_USER };
      }
      const message = err.message || 'Login failed';
      setError(message);
      setLoading(false);
      return { success: false, message };
    }
  };

  const register = async (userData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.auth.register(userData);
      if (response.success) {
        const { user: newUser, salon: newSalon, token: newToken } = response.data;
        localStorage.setItem('token', newToken);
        setToken(newToken);
        setUser(newUser);
        setSalon(newSalon);
        setUseDemoMode(false);
        setLoading(false);
        return { success: true, user: newUser };
      }
    } catch (err) {
      const message = err.message || 'Registration failed';
      setError(message);
      setLoading(false);
      return { success: false, message };
    }
  };

  const logout = async () => {
    try {
      if (!useDemoMode) await api.auth.logout();
    } catch (e) { /* ignore */ }
    localStorage.removeItem('token');
    cacheUser(null);
    setToken(null);
    setUser(null);
    setSalon(null);
    setError(null);
  };

  const updateProfile = async (updates) => {
    try {
      if (useDemoMode) {
        const updatedUser = { ...user, ...updates };
        setUser(updatedUser);
        return { success: true, user: updatedUser };
      }
      const response = await api.auth.updateProfile(updates);
      if (response.success) {
        setUser(response.data);
        return { success: true, user: response.data };
      }
    } catch (err) {
      return { success: false, message: err.message };
    }
  };

  const changePassword = async (data) => {
    try {
      if (useDemoMode) return { success: true };
      const response = await api.auth.changePassword(data);
      return { success: response.success, message: response.message };
    } catch (err) {
      return { success: false, message: err.message };
    }
  };

  const isAuthenticated = !!token && !!user;

  return (
    <AuthContext.Provider value={{
      user, salon, token, loading, error, isAuthenticated, useDemoMode,
      login, register, logout, updateProfile, changePassword, setError, setUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
